import { Button, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { buildProgramProgressText } from "@/pages/CustomerDashboard/CustomerDashboard";
import { getNextTier, getRankEarned, getTier } from "@/pages/FarmerList/helper";
import { RewardsProgramProgressModal } from '@/pages/FarmerList/RewardsProgramProgressModal';
import { getCustomerDashboardUrl } from '@/utilities/CustomerDashboardUrl';
import { ApiRetailer, ApiUser } from '@api/interfaces';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import BarChart from "@mui/icons-material/BarChart";
import { Stack } from '@mui/material';
import { formatCurrency, use } from "@shared/utilities";
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RewardsProgramStatusProps {
  customer: ApiUser,
  farmerRewardsProfiles?: ApiFarmerRewards[],
  retailer: ApiRetailer,
  rewardsProgram?: ApiRewardsProgram,
}

export const RewardsProgramStatus = ({
  customer,
  farmerRewardsProfiles,
  retailer,
  rewardsProgram,
}: RewardsProgramStatusProps) => {
  const { t } = useTranslation();
  const [showProgressModal, setShowProgressModal] = useState(false);

  const farmerRewards = farmerRewardsProfiles?.find(
    (rewards) => rewards.programId === rewardsProgram?.id,
  ) ?? null;

  const rankEarned = rewardsProgram ? getRankEarned(farmerRewards, rewardsProgram) : null;
  const earnedTier = (farmerRewards && rankEarned !== null) ?
    getTier(rankEarned, rewardsProgram?.tiers ?? []) : null;

  const nextTier = getNextTier(rankEarned, rewardsProgram?.tiers ?? []);
  const totalQualifyingSpend = formatCurrency(farmerRewards?.qualifyingTotalSpend ?? 0, i18next);
  const totalSpendGoal = nextTier ? nextTier.minimumDollars : earnedTier?.minimumDollars ?? 0;
  const totalSegmentsHurdled = farmerRewards?.segmentsHurdled ?? 0;
  const totalSegmentsGoal = nextTier?.minimumSegments ?? 0;

  const customerProgressAndDashboardButtons = use(customer, (customer) => {
    const { dashboardAccessToken, isHistorical } = customer;
    const { shortUrlName } = retailer;
    if (!dashboardAccessToken || !shortUrlName || isHistorical) {
      return <></>;
    }

    const showCustomerProgressButton = rankEarned !== null && !retailer.isProgramCreationDisabled;
    const customerProgressButton = showCustomerProgressButton &&
      <>
        <Button
          onClick={() => setShowProgressModal(true)}
          size="small"
          square
          startIcon={<BarChart/>}
          testID="view-progress-button"
        >
          {t($ => $.customers.rewardsProgram.viewProgress)}
        </Button>
        <HSpacer size="4"/>
      </>;

    const customerDashboardButton = <Button
      onClick={() => {
        const customerDashboardUrl = getCustomerDashboardUrl(shortUrlName, dashboardAccessToken);
        window.open(customerDashboardUrl, '_blank');
      }}
      size="small"
      square
      testID="customer-dashboard-button"
      variant="outlined"
    >
      {t($ => $.customers.rewardsProgram.customerDashboard)}
    </Button>;

    return <>
      <VSpacer size="5" />
      <Stack direction="row">
        {customerProgressButton}
        {customerDashboardButton}
      </Stack>
    </>;
  });

  return (
    <Stack>
      {!!rewardsProgram && !retailer.isProgramCreationDisabled && (
        <>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Text category="body-xlarge">
                {rewardsProgram.name ?? 'Rewards Program'}
              </Text>
              <Text category="body-small">
                {buildProgramProgressText(farmerRewards, rewardsProgram, t)}
              </Text>
            </Stack>
          </Stack>
          <VSpacer size="4" />
          {!earnedTier && (
            <Stack direction="row">
              <Text category="body-medium">
                <b>{t($ => $.customers.rewardsProgram.notEnrolled)}</b>
              </Text>
            </Stack>
          )}
          {!!earnedTier && (
            <>
              <Stack direction="row">
                <Text category="body-medium">
                  {t($ => $.customers.rewardsProgram.reEarnedTier)}{' '}<b>{earnedTier.name}</b>
                </Text>
              </Stack>
              {!!nextTier && !!totalSpendGoal && (
                <Stack direction="row">
                  <Text category="body-medium">
                    {t($ => $.customers.rewardsProgram.customerHasSpent)}{" "}
                    <b>{totalQualifyingSpend}</b>
                    {" "}{t($ => $.customers.rewardsProgram.towardsNextTierGoalOf)}{" "}
                    <b>{formatCurrency(totalSpendGoal, i18next)}</b>
                  </Text>
                </Stack>
              )}
              {!!nextTier && !!totalSegmentsGoal && (
                <Stack direction="row">
                  <Text category="body-medium">
                    {t($ => $.customers.rewardsProgram.customerHasHurdled)}{" "}
                    <b>
                      {totalSegmentsHurdled}
                      {" "}
                      {totalSegmentsHurdled === 1
                        ? t($ => $.common.units.category)
                        : t($ => $.common.units.categories)
                      }
                    </b>
                    {" "}{t($ => $.customers.rewardsProgram.towardsNextTierGoalOf)}{" "}
                    <b>{totalSegmentsGoal} {totalSegmentsGoal === 1
                      ? t($ => $.common.units.category)
                      : t($ => $.common.units.categories)
                    }</b>
                  </Text>
                </Stack>
              )}
              {!nextTier && (
                <>
                  <Stack direction="row">
                    <Text category="body-medium">
                      {t($ => $.customers.rewardsProgram.toMaintain)} {earnedTier.name},
                      {!!earnedTier.minimumDollars && (
                        <>
                          {" "}{t($ => $.customers.rewardsProgram.spend)}{" "}
                          <b>{formatCurrency(earnedTier.minimumDollars, i18next)}</b>
                        </>
                      )}
                      {!!earnedTier.minimumDollars && !!earnedTier.minimumSegments && (
                        <>
                          {" "}{t($ => $.customers.rewardsProgram.andHurdle)}{" "}
                          <b>
                            {earnedTier.minimumSegments}
                            {" "}
                            {earnedTier.minimumSegments === 1
                              ? t($ => $.common.units.category)
                              : t($ => $.common.units.categories)
                            }
                          </b>
                        </>
                      )}
                      {!earnedTier.minimumDollars && !!earnedTier.minimumSegments && (
                        <>
                          {" "}{t($ => $.customers.rewardsProgram.hurdle)}{" "}
                          <b>
                            {earnedTier.minimumSegments}
                            {" "}
                            {earnedTier.minimumSegments === 1
                              ? t($ => $.common.units.category)
                              : t($ => $.common.units.categories)
                            }
                          </b>
                        </>
                      )}
                    </Text>
                  </Stack>
                  {!!earnedTier.minimumDollars && (
                    <Stack direction="row">
                      <Text category="body-medium">
                        {t($ => $.customers.rewardsProgram.currentSpend)}{' '}
                        <b>{totalQualifyingSpend}</b>
                      </Text>
                    </Stack>
                  )}
                  {!!earnedTier.minimumSegments && (
                    <Stack direction="row">
                      <Text category="body-medium">
                        {t($ => $.customers.rewardsProgram.categoriesHurdled)}{" "}
                        <b>
                          {totalSegmentsHurdled} {totalSegmentsHurdled === 1
                            ? t($ => $.common.units.category)
                            : t($ => $.common.units.categories)
                        }
                        </b>
                      </Text>
                    </Stack>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      {customerProgressAndDashboardButtons}
      {!!rewardsProgram && (
        <RewardsProgramProgressModal
          customer={customer}
          farmerRewards={farmerRewards}
          farmerRewardsProfiles={farmerRewardsProfiles}
          onClose={() => setShowProgressModal(false)}
          open={showProgressModal}
          rewardsProgram={rewardsProgram}
        />
      )}
    </Stack>
  );
};
